// src/Store/templateSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import Unauthorized from '../Admin/Unauthorized'; // adjust path as needed
import BaseUrl from '../../BaseUrl';

const headers = Unauthorized.Actionsauthorized();
const url =BaseUrl.authurl
const templatesUrl = BaseUrl.templatesurl

// ---------- Thunks ----------
export const fetchDepartments = createAsyncThunk(
  'templates/fetchDepartments',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(url + 'Departments/GetAllDepartments', { headers });
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const fetchTemplatesByDepartmentId = createAsyncThunk(
  'templates/fetchTemplatesByDepartmentId',
  async (departmentId, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${templatesUrl}Templates/FindByDepartmentId?departmentId=${departmentId}`,
        { headers }
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const addTemplate = createAsyncThunk(
  'templates/addTemplate',
  async (templateData, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${templatesUrl}Templates/AddTemplate`,
        templateData,
        { headers }
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const updateTemplate = createAsyncThunk(
  'templates/updateTemplate',
  async ({ templateId, templateData }, { rejectWithValue }) => {
    try {
      
      const response = await axios.put(
        `${templatesUrl}Templates/UpdateTemplate?templateId=${templateId}`,
        templateData,
        { headers }
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// ---------- New Delete Thunk ----------
export const deleteTemplate = createAsyncThunk(
  'templates/deleteTemplate',
  async (templateId, { rejectWithValue }) => {
    try {
      const response = await axios.delete(
        `${templatesUrl}Templates/DeleteTemplate?templateId=${templateId}`,
        { headers }
      );
      return { templateId, data: response.data }; // return templateId for potential optimistic update
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// ---------- Initial State ----------
const initialState = {
  // All departments
  departments: [],
  status: 'idle',
  error: null,

  // Templates for a specific department
  departmentTemplates: [],
  departmentTemplatesStatus: 'idle',
  departmentTemplatesError: null,

  // Add template
  addStatus: 'idle',
  addError: null,

  // Update template
  updateStatus: 'idle',
  updateError: null,

  // Delete template
  deleteStatus: 'idle',
  deleteError: null,
};

// ---------- Slice ----------
const templateSlice = createSlice({
  name: 'templates',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // fetchDepartments
      .addCase(fetchDepartments.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchDepartments.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.departments = action.payload;
      })
      .addCase(fetchDepartments.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })

      // fetchTemplatesByDepartmentId
      .addCase(fetchTemplatesByDepartmentId.pending, (state) => {
        state.departmentTemplatesStatus = 'loading';
        state.departmentTemplatesError = null;
      })
      .addCase(fetchTemplatesByDepartmentId.fulfilled, (state, action) => {
        state.departmentTemplatesStatus = 'succeeded';
        state.departmentTemplates = action.payload;
      })
      .addCase(fetchTemplatesByDepartmentId.rejected, (state, action) => {
        state.departmentTemplatesStatus = 'failed';
        state.departmentTemplatesError = action.payload;
      })

      // addTemplate
      .addCase(addTemplate.pending, (state) => {
        state.addStatus = 'loading';
        state.addError = null;
      })
      .addCase(addTemplate.fulfilled, (state, action) => {
        state.addStatus = 'succeeded';
        // Optionally push the new template to departmentTemplates if it's for the same department
        // (you would need to check departmentId)
      })
      .addCase(addTemplate.rejected, (state, action) => {
        state.addStatus = 'failed';
        state.addError = action.payload;
      })

      // updateTemplate
      .addCase(updateTemplate.pending, (state) => {
        state.updateStatus = 'loading';
        state.updateError = null;
      })
      .addCase(updateTemplate.fulfilled, (state, action) => {
        state.updateStatus = 'succeeded';
        // Update the template in the local list if present
        const updated = action.payload;
        const index = state.departmentTemplates.findIndex(t => t.Id === updated.Id);
        if (index !== -1) {
          state.departmentTemplates[index] = updated;
        }
      })
      .addCase(updateTemplate.rejected, (state, action) => {
        state.updateStatus = 'failed';
        state.updateError = action.payload;
      })

      // deleteTemplate
      .addCase(deleteTemplate.pending, (state) => {
        state.deleteStatus = 'loading';
        state.deleteError = null;
      })
      .addCase(deleteTemplate.fulfilled, (state, action) => {
        state.deleteStatus = 'succeeded';
        // Optionally remove the template optimistically:
        // const { templateId } = action.payload;
        // state.departmentTemplates = state.departmentTemplates.filter(t => t.Id !== templateId);
        // We'll refetch after delete instead to keep it simple.
      })
      .addCase(deleteTemplate.rejected, (state, action) => {
        state.deleteStatus = 'failed';
        state.deleteError = action.payload;
      });
  },
});

export default templateSlice.reducer;